import { isStatsigInitialized } from '@eventbrite/statsig';
import React from 'react';
import type {
    Event,
    FeatureFlags,
    LegacyConversionBarProps,
} from '../../../types/index';
import { DiscountType } from '../../../types/index';
import {
    ConversionBar,
    ConversionBarComponentProps,
} from './ConversionBar/ConversionBar';
import './ConversionBarContainer.scss';
import { DiscountSignal } from './DiscountSignal';
import {
    isEventEnabledInMultiTicketsExperiment,
    isTicketSelectionExcludedInListings,
} from './isTicketSelectionExcludedInListings';
import { TicketSelectionIframe } from './TicketSelectionIframe/TicketSelectionIframe';

import { animated, useChain, useSpring, useSpringRef } from 'react-spring';

export interface ConversionBarContainerProps
    extends ConversionBarComponentProps,
        LegacyConversionBarProps {
    compactCheckoutDisqualifications: Event['compactCheckoutDisqualifications'];
    ticketsInfo: Event['ticketsInfo'];
    salesStatus: Event['salesStatus']['sales_status'];
    discount?: Event['discount'];
    enableOpenDiscountsOnListings: FeatureFlags['enableOpenDiscountsOnListings'];
    isSalesEndedListingExperimentEnabled: boolean;
    handleExploreSimilareEventsButtonClick: Function;
}

export const ConversionBarContainer = (props: ConversionBarContainerProps) => {
    const isMultiTicketEnabled = isEventEnabledInMultiTicketsExperiment();
    const ticketSelectionIframe = (
        <TicketSelectionIframe
            eventId={props.eventId}
            eventName={props.eventName as string}
            affiliateCode={props.affiliateCode}
            isPreview={props.isPreview}
            promoCode={props.promoCode}
            waitlistToken={props.waitlistToken}
            rsvpToken={props.rsvpToken}
            campaign_token={props.campaign_token}
        />
    );
    const isTicketSelectionExcluded = isTicketSelectionExcludedInListings({
        soldOut: props.compactCheckoutDisqualifications.sold_out,
        reservedSeating:
            props.compactCheckoutDisqualifications.reserved_seating,
        moreThanOneTicket:
            props.compactCheckoutDisqualifications.more_than_one_ticket,
        isSalesEnded: props.compactCheckoutDisqualifications.is_sales_ended,
        isRsvp: props.compactCheckoutDisqualifications.is_rsvp,
        isPostponed: props.compactCheckoutDisqualifications.is_postponed,
        isCanceled: props.compactCheckoutDisqualifications.is_canceled,
        hasWaitlist: props.compactCheckoutDisqualifications.has_waitlist,
        hasWaitingRoom: props.compactCheckoutDisqualifications.has_waiting_room,
        hasDiscounts: props.compactCheckoutDisqualifications.has_discounts,
        hasAddons: props.compactCheckoutDisqualifications.has_addons,
        discountCode: props.compactCheckoutDisqualifications.discount_code,
        hasDonations: props.compactCheckoutDisqualifications.has_donations,
        hasInventoryTiers:
            props.compactCheckoutDisqualifications.has_inventory_tiers,
        externalTicketsAreAvailable: props.externalTicketsAreAvailable,
        salesStatus: props.salesStatus,
        isSeriesParent: props.isSeriesParent,
    });

    const springRef = useSpringRef();
    const [containerSprings] = useSpring(
        () => ({
            ref: springRef,
            from: { transform: 'translateY(180px)' },
            to: { transform: 'translateY(0)' },
            config: {
                damping: 15,
                tension: 100,
                precision: 0.0001,
            },
        }),
        [],
    );
    const transRef = useSpringRef();
    const [signalSprings] = useSpring(
        () => ({
            ref: transRef,
            from: { transform: 'translateY(32px)' },
            to: { transform: 'translateY(0)' },
            config: {
                damping: 24,
                tension: 256,
            },
        }),
        [],
    );

    const { discount, enableOpenDiscountsOnListings } = props;
    const discountApplied = discount?.percentOff || discount?.amountOff;
    const shouldShowOpenDiscount =
        enableOpenDiscountsOnListings &&
        discount?.discountType === DiscountType.open &&
        discountApplied;

    useChain([springRef, transRef], [0, 1], 1500);

    if (!isStatsigInitialized()) {
        return null;
    }

    const ConversionBarContent: React.FC = () => {
        if (!isTicketSelectionExcluded) {
            return <>{ticketSelectionIframe}</>;
        }

        return isMultiTicketEnabled ? (
            <>{ticketSelectionIframe}</>
        ) : (
            <ConversionBar {...props} />
        );
    };

    return (
        <animated.div
            style={containerSprings}
            className="conversion-bar-container"
        >
            {shouldShowOpenDiscount && (
                <animated.div
                    style={signalSprings}
                    className="conversion-bar-signal"
                >
                    <DiscountSignal discountApplied={discountApplied} />
                </animated.div>
            )}
            <div style={{ position: 'relative' }}>
                <ConversionBarContent />
            </div>
        </animated.div>
    );
};
